import React, { useEffect, useState } from 'react';
import { Loader2, Scale } from 'lucide-react';
import api from '../services/api';
import TradosPublicHeader from '../components/TradosPublicHeader';
import useCanonical from '../hooks/useCanonical';

const DEFAULT_DISCLAIMER = `
<h2>Disclaimer</h2>
<p>Trados provides its Letter of Intent (LOI) creation and verification tools as a technology service only.</p>

<h3>No Ownership or Responsibility</h3>
<p>Trados assumes no ownership, responsibility, or liability for the content, accuracy, completeness, or legality of any LOI created using the platform. All content within any LOI document is solely the responsibility of the parties involved in its creation.</p>

<h3>No Endorsement or Guarantee</h3>
<p>Trados does not verify, endorse, or guarantee any of the information, terms, representations, or commitments contained within LOI documents created through our platform. The use of Trados branding on a document (including the "Created by Trados" badge) indicates only that the document was generated using our tools — it does not constitute an endorsement of the document's contents or the parties involved.</p>

<h3>User Responsibility</h3>
<p>Users are solely responsible for ensuring that all information provided in their LOI documents is accurate, truthful, and compliant with all applicable laws and regulations. Users should seek independent legal, financial, and professional advice before entering into any commitments based on LOI documents.</p>

<h3>Limitation of Liability</h3>
<p>To the fullest extent permitted by law, Trados shall not be held liable for any losses, damages, claims, or expenses arising from the use of or reliance on any LOI document created using our platform, including but not limited to direct, indirect, incidental, consequential, or punitive damages.</p>

<h3>Verification Services</h3>
<p>Trados LOI verification services provide automated analysis for informational purposes only. Verification results do not constitute legal or professional opinions. Users should independently verify all information and consult qualified professionals before making decisions based on verification reports.</p>

<h3>Changes to This Disclaimer</h3>
<p>Trados reserves the right to update this disclaimer at any time. Continued use of the platform constitutes acceptance of the current disclaimer terms.</p>

<p><strong>Last updated:</strong> March 2026</p>
<p>For questions, contact us at <a href="mailto:support@trados.app">support@trados.app</a></p>
`;

const DisclaimerPage = () => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  useCanonical('/disclaimer');

  useEffect(() => {
    const fetchDisclaimer = async () => {
      try {
        const res = await api.get('/content/loi_disclaimer');
        if (res.data && res.data.content_value) {
          setContent(res.data.content_value);
        } else {
          setContent(DEFAULT_DISCLAIMER);
        }
      } catch {
        setContent(DEFAULT_DISCLAIMER);
      } finally {
        setLoading(false);
      }
    };
    fetchDisclaimer();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TradosPublicHeader />
        <div className="flex items-center justify-center p-4" style={{ minHeight: 'calc(100vh - 56px)' }}>
          <Loader2 className="w-8 h-8 text-[#2B4B6A] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TradosPublicHeader />
      <div className="py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-[#2B4B6A] px-8 py-7 text-center text-white">
            <div className="w-12 h-12 bg-white/15 rounded-xl mx-auto flex items-center justify-center mb-3">
              <Scale className="w-6 h-6" />
            </div>
            <h1 className="text-xl font-bold">Legal Disclaimer</h1>
            <p className="text-sm opacity-80 mt-1">Trados LOI Platform</p>
          </div>

          {/* Content */}
          <div
            className="px-8 py-8 prose prose-sm prose-gray max-w-none
              [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-gray-800 [&_h2]:mb-4
              [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-gray-700 [&_h3]:mt-6 [&_h3]:mb-2
              [&_p]:text-sm [&_p]:text-gray-600 [&_p]:leading-relaxed [&_p]:mb-3
              [&_a]:text-[#2B4B6A] [&_a]:underline
              [&_strong]:text-gray-700"
            dangerouslySetInnerHTML={{ __html: content }}
          />

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-5 text-center text-xs text-gray-400">
            © 2026 Trados — The Trusted Trade Platform —{' '}
            <a href="https://www.trados.app" className="text-gray-400 hover:underline">
              www.trados.app
            </a>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default DisclaimerPage;
